import { useQueryClient } from "@tanstack/react-query";
import { Loader2, LogOut } from "lucide-react";
import { Navigate, useNavigate } from "react-router-dom";
import { api, setSite } from "../../lib/api";
import { PLATFORM, PoweredBy, usePlatformTheme } from "../../lib/platformBrand";
import { useMe } from "../admin/AdminDashboard";
import { SitesPanel } from "./SitesPanel";
import { EresDbPanel } from "./EresDbPanel";
import { PlatformPanel } from "./PlatformPanel";
import { SystemSmtpPanel } from "./SystemSmtpPanel";
import { LogsPanel } from "./LogsPanel";
import { StoragePanel } from "./StoragePanel";
import { GooglePanel } from "./GooglePanel";
import { WebsitesPanel } from "./WebsitesPanel";
import { ChangePasswordButton } from "../admin/ChangePassword";

export default function SuperadminPage() {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  usePlatformTheme();
  setSite("");
  if (me.isError) return <Navigate to="/admin/login" replace />;
  if (!me.data) return <div className="grid min-h-screen place-items-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;
  if (me.data.platform_role !== "superadmin") return <Navigate to="/admin" replace />;
  const logout = async () => {
    await api.post("/auth/logout");
    qc.removeQueries();
    navigate("/admin/login");
  };
  return (
    <div className="min-h-screen bg-linen font-sans text-ink" data-testid="superadmin-page">
      <header className="sticky top-0 z-40 border-b border-ink/10 bg-white/90 backdrop-blur-xl">
        <div className="container-x flex items-center justify-between gap-3 py-3">
          <div className="flex min-w-0 items-center gap-4">
            <img src={PLATFORM.logo} alt={PLATFORM.name} className="h-9 w-auto shrink-0 sm:h-12" data-testid="superadmin-logo" />
            <div className="min-w-0 border-l border-ink/10 pl-4">
              <p className="font-serif text-xl font-bold leading-tight text-brand">{PLATFORM.product}</p>
              <p className="truncate font-mono text-[10px] uppercase tracking-[0.25em] text-ink/50">Superadmin · {me.data.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <ChangePasswordButton />
            <button className="btn-ghost" onClick={logout} title="Sign out" data-testid="superadmin-logout"><LogOut className="h-4 w-4" />Sign out</button>
          </div>
        </div>
      </header>
      <div className="container-x space-y-8 py-8">
        <div className="relative overflow-hidden rounded-3xl bg-brand p-8 text-white" data-testid="superadmin-hero">
          <div aria-hidden="true" className="absolute -right-20 -top-24 h-72 w-72 rounded-full bg-[radial-gradient(circle,rgba(58,176,225,0.4),transparent_65%)]" />
          <p className="relative font-mono text-[11px] uppercase tracking-[0.3em] text-white/60">{PLATFORM.name}</p>
          <h1 className="relative mt-2 font-serif text-3xl font-bold sm:text-4xl">Hotel booking sites, <span className="text-gold">one platform.</span></h1>
          <p className="relative mt-3 max-w-2xl text-sm font-light text-white/75">Create a site for any property on eRes, hand over the login, and help hotels customise their page whenever they ask.</p>
        </div>
        <SitesPanel />
        <WebsitesPanel />
        <EresDbPanel />
        <PlatformPanel />
        <SystemSmtpPanel />
        <StoragePanel />
        <GooglePanel />
        <LogsPanel />
        <div className="pb-4 text-center"><PoweredBy /></div>
      </div>
    </div>
  );
}
